/**
 * Customer Account — storefront JS client for the aServer customer-account API.
 * All requests go to {API_BASE}/storefront/account/* with credentials:'include'
 * with an optional tab-scoped bearer session for browsers blocking third-party cookies.
 *
 * Usage from Liquid sections (always inside DOMContentLoaded):
 *
 *   const me = await window.NeviosAccount.me();
 *   if (!me) location.href = '/pages/account-login';
 *   await window.NeviosAccount.login({ email, password });
 *   await window.NeviosAccount.logout();
 *   await window.NeviosAccount.api('GET', '/profile');
 *
 * The window.NEVIOS_API_BASE override lets the theme target a staging server.
 */
(function () {
  const DEFAULT_API_BASE = 'https://vasky-nevios-express-production.up.railway.app/api';
  const API_BASE = (window.NEVIOS_API_BASE || DEFAULT_API_BASE).replace(/\/$/, '');

  // Keep the bearer out of URLs and persistent localStorage. It lasts only in
  // this storefront tab; HttpOnly cookies remain available to other browsers.
  const SESSION_KEY = 'nv_customer_tab_session';
  function readSession() {
    try {
      const value = JSON.parse(sessionStorage.getItem(SESSION_KEY));
      if (value && /^[A-Za-z0-9_-]{43}$/.test(value.token) && Date.parse(value.expires_at) > Date.now()) return value;
      sessionStorage.removeItem(SESSION_KEY);
    } catch (_) { /* storage unavailable */ }
    return null;
  }
  function clearSession() {
    try { sessionStorage.removeItem(SESSION_KEY); } catch (_) {}
  }
  function saveSession(data) {
    if (!data?.token) return; // Compatible with the backend during deployment.
    try { sessionStorage.setItem(SESSION_KEY, JSON.stringify({ token: data.token, expires_at: data.expires_at })); }
    catch (_) { /* the existing cookie path may still work */ }
  }

  async function api(method, path, body) {
    const opts = {
      method,
      credentials: 'include',
      headers: { 'Accept': 'application/json' },
    };
    const session = readSession();
    if (session) opts.headers.Authorization = `Bearer ${session.token}`;
    if (body !== undefined) {
      opts.headers['Content-Type'] = 'application/json';
      opts.body = JSON.stringify(body);
    }
    const url = `${API_BASE}/storefront/account${path}`;
    let res, data;
    try {
      res = await fetch(url, opts);
    } catch (e) {
      throw new Error('Síťová chyba — zkuste to prosím znovu.');
    }
    try { data = await res.json(); } catch { data = null; }
    if (res.status === 401) clearSession();
    if (!res.ok) {
      const msg = data?.error || `HTTP ${res.status}`;
      const err = new Error(msg);
      err.status = res.status;
      err.data = data;
      throw err;
    }
    if (path === '/auth/magic-link/confirm') saveSession(data?.data);
    return data;
  }

  /** Quick auth check. Returns customer object or null. */
  async function me() {
    try {
      const r = await api('GET', '/auth/me');
      return r?.data || null;
    } catch (e) {
      if (e.status === 401) return null;
      throw e;
    }
  }

  async function login(payload) {
    const r = await api('POST', '/auth/login', payload);
    return r?.data;
  }

  async function logout() {
    await api('POST', '/auth/logout', {});
    clearSession();
    window.dispatchEvent(new CustomEvent('nv:account-changed'));
  }

  async function register(payload) {
    const r = await api('POST', '/auth/register', payload);
    return r?.data;
  }

  async function requestPasswordReset(email) {
    return await api('POST', '/auth/password-reset/request', { email });
  }

  async function confirmPasswordReset(token, password) {
    return await api('POST', '/auth/password-reset/confirm', { token, password });
  }

  async function verifyEmail(token) {
    return await api('POST', '/auth/verify-email', { token });
  }

  async function changePassword(currentPassword, newPassword) {
    return await api('POST', '/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
  }

  /** Redirect to login if not authenticated. Returns the customer object once known. */
  async function requireAuth(loginPath = '/pages/account-login') {
    const customer = await me();
    if (!customer) {
      const next = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.href = `${loginPath}?next=${next}`;
      return null;
    }
    return customer;
  }

  /** Format CZK / EUR amount nicely. */
  function formatPrice(amount, currency = 'CZK') {
    const n = Number(amount) || 0;
    try {
      return new Intl.NumberFormat('cs-CZ', { style: 'currency', currency, maximumFractionDigits: 0 }).format(n);
    } catch {
      return `${n.toFixed(0)} ${currency}`;
    }
  }

  function formatDate(iso) {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString('cs-CZ', { day: 'numeric', month: 'long', year: 'numeric' });
    } catch { return iso; }
  }

  /** Status → Czech human label + colour class for badges. */
  function orderStatusLabel(status, paymentStatus, fulfillmentStatus) {
    const map = {
      ACTIVE: { label: 'Aktivní', cls: 'nca-badge--active' },
      ARCHIVED: { label: 'Vyřízeno', cls: 'nca-badge--archived' },
      HOLD: { label: 'Čeká na platbu', cls: 'nca-badge--hold' },
    };
    const m = map[status] || { label: status, cls: '' };
    let extra = '';
    if (paymentStatus === 'PAID') extra = '· Zaplaceno';
    else if (paymentStatus === 'UNPAID') extra = '· Nezaplaceno';
    if (fulfillmentStatus === 'FULFILLED') extra = '· Odesláno';
    return { label: m.label, cls: m.cls, extra };
  }

  /** Read URL query param. */
  function getQuery(name) {
    return new URLSearchParams(window.location.search).get(name);
  }

  window.NeviosAccount = {
    api, me, login, logout, register,
    requestPasswordReset, confirmPasswordReset, verifyEmail, changePassword,
    requireAuth,
    formatPrice, formatDate, orderStatusLabel, getQuery,
    API_BASE,
  };
})();
